import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { I18nProvider, useI18n } from "@/lib/i18n";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminClients from "@/pages/AdminClients";
import AdminClientDetail from "@/pages/AdminClientDetail";
import AdminPriceLists from "@/pages/AdminPriceLists";
import AdminOrders from "@/pages/AdminOrders";
import AdminTransactions from "@/pages/AdminTransactions";
import AdminRecharges from "@/pages/AdminRecharges";
import AdminSimulate from "@/pages/AdminSimulate";
import AdminTickets from "@/pages/AdminTickets";
import AdminMyzeLog from "@/pages/AdminMyzeLog";
import AdminMyzeImport from "@/pages/AdminMyzeImport";
import AdminPendingActivations from "@/pages/AdminPendingActivations";
import AdminBranding from "@/pages/AdminBranding";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import ClientDashboard from "@/pages/ClientDashboard";
import ClientRecharge from "@/pages/ClientRecharge";
import ClientOrders from "@/pages/ClientOrders";
import ClientPriceList from "@/pages/ClientPriceList";
import ClientAutoRecharge from "@/pages/ClientAutoRecharge";
import Signup, { SignupSuccess } from "@/pages/Signup";
import { LegalTerms, LegalPrivacy, LegalCookies } from "@/pages/Legal";
import CookieBanner from "@/components/CookieBanner";
import ClientTransactions from "@/pages/ClientTransactions";
import ClientInvoices from "@/pages/ClientInvoices";
import ClientSupport from "@/pages/ClientSupport";
import ClientProfile from "@/pages/ClientProfile";
import ClientSubscription from "@/pages/ClientSubscription";
import ClientBranding from "@/pages/ClientBranding";
import PaymentSuccess from "@/pages/PaymentSuccess";

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--tf-muted)]">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/client"} replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--tf-muted)]">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/client"} replace />;
}

function LanguageSync() {
  const { user } = useAuth();
  const { lang, setLang } = useI18n();
  const applied = useRef(false);
  useEffect(() => {
    if (!user?.language || applied.current) return;
    applied.current = true;
    // Only auto-apply the account's saved language if the visitor never made an explicit
    // choice in this browser (e.g. via the LanguageSwitcher on /login or /signup).
    const hasLocalPref = localStorage.getItem("tf_lang") !== null;
    if (!hasLocalPref && user.language !== lang) setLang(user.language);
  }, [user?.language]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <LanguageSync />
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Public legal pages */}
              <Route path="/legal/terms" element={<LegalTerms />} />
              <Route path="/legal/privacy" element={<LegalPrivacy />} />
              <Route path="/legal/cookies" element={<LegalCookies />} />

              {/* Public signup wizard */}
              <Route path="/signup" element={<Signup />} />
              <Route path="/signup/success" element={<SignupSuccess />} />

              <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />

              <Route path="/admin" element={<Protected role="admin"><Layout><AdminDashboard /></Layout></Protected>} />
              <Route path="/admin/clients" element={<Protected role="admin"><Layout><AdminClients /></Layout></Protected>} />
              <Route path="/admin/clients/:clientId" element={<Protected role="admin"><Layout><AdminClientDetail /></Layout></Protected>} />
              <Route path="/admin/pending-activations" element={<Protected role="admin"><Layout><AdminPendingActivations /></Layout></Protected>} />
              <Route path="/admin/pricelists" element={<Protected role="admin"><Layout><AdminPriceLists /></Layout></Protected>} />
              <Route path="/admin/orders" element={<Protected role="admin"><Layout><AdminOrders /></Layout></Protected>} />
              <Route path="/admin/transactions" element={<Protected role="admin"><Layout><AdminTransactions /></Layout></Protected>} />
              <Route path="/admin/recharges" element={<Protected role="admin"><Layout><AdminRecharges /></Layout></Protected>} />
              <Route path="/admin/tickets" element={<Protected role="admin"><Layout><AdminTickets /></Layout></Protected>} />
              <Route path="/admin/simulate" element={<Protected role="admin"><Layout><AdminSimulate /></Layout></Protected>} />
              <Route path="/admin/myze-log" element={<Protected role="admin"><Layout><AdminMyzeLog /></Layout></Protected>} />
              <Route path="/admin/myze-import" element={<Protected role="admin"><Layout><AdminMyzeImport /></Layout></Protected>} />
              <Route path="/admin/branding" element={<Protected role="admin"><Layout><AdminBranding /></Layout></Protected>} />

              <Route path="/client" element={<Protected role="client"><Layout><ClientDashboard /></Layout></Protected>} />
              <Route path="/client/recharge" element={<Protected role="client"><Layout><ClientRecharge /></Layout></Protected>} />
              <Route path="/client/orders" element={<Protected role="client"><Layout><ClientOrders /></Layout></Protected>} />
              <Route path="/client/pricelist" element={<Protected role="client"><Layout><ClientPriceList /></Layout></Protected>} />
              <Route path="/client/auto-recharge" element={<Protected role="client"><Layout><ClientAutoRecharge /></Layout></Protected>} />
              <Route path="/client/transactions" element={<Protected role="client"><Layout><ClientTransactions /></Layout></Protected>} />
              <Route path="/client/invoices" element={<Protected role="client"><Layout><ClientInvoices /></Layout></Protected>} />
              <Route path="/client/support" element={<Protected role="client"><Layout><ClientSupport /></Layout></Protected>} />
              <Route path="/client/profile" element={<Protected role="client"><Layout><ClientProfile /></Layout></Protected>} />
              <Route path="/client/subscription" element={<Protected role="client"><Layout><ClientSubscription /></Layout></Protected>} />
              <Route path="/client/branding" element={<Protected role="client"><Layout><ClientBranding /></Layout></Protected>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <CookieBanner />
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
